<template>
  <div class="field-editor">
    <!-- Top bar: document selector + zoom controls -->
    <div class="editor-topbar">
      <q-select
        v-if="documents.length > 1"
        v-model="activeDocIndex"
        :options="documentOptions"
        label="Document"
        dense
        outlined
        emit-value
        map-options
        style="min-width: 200px;"
      />
      <div v-else class="text-body2 text-weight-medium q-px-sm">
        {{ activeDocument?.name || 'Document' }}
      </div>

      <q-space />

      <transition name="fade">
        <div v-if="slowWarning" class="slow-warning text-caption text-orange-8 row items-center no-wrap">
          <q-spinner-dots size="14px" class="q-mr-xs" />
          Saving is taking longer than usual...
        </div>
      </transition>

      <div class="zoom-controls">
        <q-btn flat dense icon="remove" size="sm" @click="zoomOut" :disable="scale <= 0.5" />
        <span class="text-body2 q-mx-sm" style="min-width: 45px; text-align: center;">
          {{ Math.round(scale * 100) }}%
        </span>
        <q-btn flat dense icon="add" size="sm" @click="zoomIn" :disable="scale >= 3" />
        <q-btn flat dense label="Fit" size="sm" no-caps class="q-ml-xs" @click="zoomFit" />
      </div>
    </div>

    <!-- Main content: toolbar | PDF | properties -->
    <div class="editor-content">
      <FieldToolbar
        :signers="signers"
        :activeSignerIndex="activeSignerIndex"
        :scale="lastRenderScale"
        :referencePage="firstActivePage"
        @signer-change="activeSignerIndex = $event"
      />

      <div class="editor-center">
        <ESignDocumentViewer
          ref="docViewer"
          v-model:scale="scale"
          :documents="viewerDocuments"
          :pages-by-document="viewerPagesByDocument"
          :show-headers="false"
          :is-interactive="isFieldTarget"
        >
          <template #page-fields="{ page, containerWidth }">
            <PdfPageViewer
              :ref="el => { setPageRef(page.pageNumber, el); if (containerWidth > 0 && page.widthPt > 0) lastRenderScale = containerWidth / page.widthPt; }"
              :pageNumber="page.pageNumber"
              :pageWidthPt="page.widthPt"
              :pageHeightPt="page.heightPt"
              :scale="containerWidth / page.widthPt"
              :fields="fieldsByPage[page.pageNumber] || []"
              :selectedFieldId="selectedFieldId"
              :editable="true"
              :fieldStatuses="fieldStatuses"
              @field-select="selectedFieldId = $event"
              @field-deselect="selectedFieldId = null"
              @field-move="handleFieldMove"
              @field-resize="handleFieldResize"
              @field-drop="handleFieldDrop"
            />
          </template>
        </ESignDocumentViewer>
      </div>

      <FieldPropertiesPanel
        :field="selectedField"
        :signers="signers"
        @update="handleFieldUpdate"
        @delete="handleFieldDelete"
      />
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import FieldToolbar from './FieldToolbar.vue';
import FieldPropertiesPanel from './FieldPropertiesPanel.vue';
import ESignDocumentViewer from '../ESignDocumentViewer.vue';
import PdfPageViewer from '../pdf/PdfPageViewer.vue';
import { resolveAnnotationDefaults } from '../pdf/fieldConstants.js';
import { FieldSaveQueue } from './FieldSaveQueue.js';
import { useAnnotationFieldTypesStore } from '../../stores/annotationFieldTypes.js';

export default {
  name: 'FieldEditor',
  components: { FieldToolbar, FieldPropertiesPanel, ESignDocumentViewer, PdfPageViewer },
  props: {
    /**
     * API adapter object providing CRUD operations.
     * Expected shape: { create(fieldData), update(fieldId, fieldData), delete(fieldId) }
     * - create returns Promise<{ id }> (server-assigned ID)
     * - update returns Promise
     * - delete returns Promise
     */
    apiAdapter: { type: Object, required: true },
    /**
     * Array of documents with pre-loaded page data.
     * Each: { documentId, name, pages: [{ pageNumber, widthPt, heightPt,
     *   imageUrl, medianLineHeightPt }] }. The medianLineHeightPt field is
     * the document's body-text line height extracted at upload time and is
     * used to scale drop-time annotation defaults so they match what the
     * server resolver would produce. Null for pages with no extractable
     * text - the helper falls back to a fraction of page height.
     */
    documents: { type: Array, default: () => [] },
    /** Array of signers: [{ name, email }] */
    signers: { type: Array, default: () => [] },
    /**
     * Initial annotations from the API (AnnotationApiResponse format).
     * Mapped to internal field format on mount.
     */
    initialAnnotations: { type: Array, default: () => [] },
  },
  emits: ['annotations-changed'],
  data() {
    return {
      fields: [],
      selectedFieldId: null,
      activeSignerIndex: 0,
      activeDocIndex: 0,
      scale: 1,
      lastRenderScale: 1,
      fieldStatuses: {},
      slowWarning: false,
      // Pinia store reference; populated in created() so child components can
      // reach the field-type rules via $parent or via the store directly.
      _fieldTypesStore: null,
    };
  },
  computed: {
    activeDocument() {
      return this.documents[this.activeDocIndex] || null;
    },
    /**
     * Server-supplied per-type sizing rules (multipliers + minimums). Read
     * directly from the Pinia store so the value updates reactively once
     * ensure() resolves. Null while loading or after a fetch failure;
     * callers fall back to FALLBACK_FIELD_RULE in that case.
     */
    fieldTypeRules() {
      return this._fieldTypesStore?.types || null;
    },
    fieldTypeFallbackFraction() {
      return this._fieldTypesStore?.fallbackLineHeightFraction ?? 0.018;
    },
    /** First page of the active document - used by the toolbar to size the drag-preview ghost. */
    firstActivePage() {
      return this.activeDocument?.pages?.[0] || null;
    },
    activeDocumentFields() {
      if (!this.activeDocument) return [];
      return this.fields.filter(f => f.documentId === this.activeDocument.documentId);
    },
    /** Group active document fields by page number for slot rendering. */
    fieldsByPage() {
      const grouped = {};
      for (const field of this.activeDocumentFields) {
        const page = field.pageNumber || 1;
        if (!grouped[page]) grouped[page] = [];
        grouped[page].push(field);
      }
      return grouped;
    },
    /** Map active document to ESignDocumentViewer format. */
    viewerDocuments() {
      if (!this.activeDocument) return [];
      return [{ id: this.activeDocument.documentId, name: this.activeDocument.name }];
    },
    /** Map active document pages to ESignDocumentViewer format. */
    viewerPagesByDocument() {
      if (!this.activeDocument) return {};
      return { [this.activeDocument.documentId]: this.activeDocument.pages || [] };
    },
    selectedField() {
      if (!this.selectedFieldId) return null;
      return this.fields.find(f => f.id === this.selectedFieldId) || null;
    },
    documentOptions() {
      return this.documents.map((doc, i) => ({
        value: i,
        label: doc.name || `Document ${i + 1}`,
      }));
    },
  },
  watch: {
    initialAnnotations: {
      handler(newVal) {
        this.fields = (newVal || []).map(this.apiToField);
      },
      immediate: true,
    },
    apiAdapter(newAdapter) {
      if (this._saveQueue) {
        this._saveQueue.setApiAdapter(newAdapter);
      }
    },
  },
  created() {
    // Load the server-owned field-type sizing rules. Fire-and-forget - the
    // user can drop fields immediately and we'll have rules within tens of
    // ms. If the fetch fails, FALLBACK_FIELD_RULE in fieldConstants.js
    // takes over (degraded but functional). Store is reactive so resize
    // handles and drop defaults pick up real values once ensure() resolves.
    this._fieldTypesStore = useAnnotationFieldTypesStore();
    this._fieldTypesStore.ensure();

    this._saveQueue = new FieldSaveQueue(this.apiAdapter, {
      debounceMs: 500,
      maxRetries: 3,
      slowThresholdMs: 4000,
      onStatusChange: (fieldId, status) => {
        this.fieldStatuses = { ...this.fieldStatuses, [fieldId]: status };
        this.slowWarning = this._saveQueue.isSlowWarningActive;
      },
      onIdSwap: (tempId, serverId) => {
        const field = this.fields.find(f => f.id === tempId);
        if (field) {
          field.id = serverId;
          if (this.selectedFieldId === tempId) {
            this.selectedFieldId = serverId;
          }
        }
        // Move status tracking to new ID
        const newStatuses = { ...this.fieldStatuses };
        const status = newStatuses[tempId];
        delete newStatuses[tempId];
        if (status) newStatuses[serverId] = status;
        this.fieldStatuses = newStatuses;
        this.emitChanged();
      },
      onPermanentFailure: (fieldId, operation, error) => {
        console.error(`[FieldEditor] Permanent failure for ${operation.type} on field ${fieldId}:`, error);
        if (operation.type === 'create') {
          const idx = this.fields.findIndex(f => f.id === fieldId);
          if (idx !== -1) {
            this.fields.splice(idx, 1);
            if (this.selectedFieldId === fieldId) this.selectedFieldId = null;
            this.emitChanged();
          }
        }
        const serverMsg = error?.response?.data?.message || error?.displayMessage;
        this.$q?.notify?.({
          type: 'negative',
          message: serverMsg || `Failed to ${operation.type} field.`,
        });
      },
    });
  },
  mounted() {
    this._keyHandler = (e) => {
      // Delete selected field on Delete/Backspace, but not when typing in an input
      if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedFieldId) {
        const tag = e.target?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
        e.preventDefault();
        this.handleFieldDelete(this.selectedFieldId);
      }
    };
    document.addEventListener('keydown', this._keyHandler);
  },
  beforeUnmount() {
    this._saveQueue?.destroy();
    if (this._keyHandler) document.removeEventListener('keydown', this._keyHandler);
  },
  methods: {
    // --- Coordinate mapping ---
    apiToField(ann) {
      const field = {
        id: ann.id,
        documentId: ann.documentId,
        type: ann.type,
        signerIndex: ann.signerIndex,
        colorIndex: ann.colorIndex,
        pageNumber: ann.pageNumber,
        x: ann.x,
        y: ann.y,
        widthPt: ann.width,
        heightPt: ann.height,
        required: ann.required,
        mustBeChecked: ann.mustBeChecked !== false,
        label: ann.label,
        group: ann.group,
        options: ann.options,
        defaultValue: ann.defaultValue,
      };
      if (ann.type === 'Dropdown') {
        console.log('[FieldEditor.apiToField] Dropdown ann.options=', JSON.stringify(ann.options), '→ field.options=', JSON.stringify(field.options));
      }
      return field;
    },
    fieldToApiCreate(field) {
      return {
        documentId: field.documentId,
        type: field.type,
        signerIndex: field.signerIndex,
        pageNumber: field.pageNumber,
        x: field.x,
        y: field.y,
        width: field.widthPt,
        height: field.heightPt,
        required: field.required,
        mustBeChecked: field.mustBeChecked,
        label: field.label,
        defaultValue: field.defaultValue != null ? field.defaultValue : undefined,
        options: field.options?.length ? field.options : undefined,
        group: field.group || undefined,
      };
    },
    fieldToApiUpdate(field) {
      return {
        signerIndex: field.signerIndex,
        pageNumber: field.pageNumber,
        x: field.x,
        y: field.y,
        width: field.widthPt,
        height: field.heightPt,
        required: field.required,
        mustBeChecked: field.mustBeChecked,
        label: field.label,
        defaultValue: field.defaultValue != null ? field.defaultValue : undefined,
        options: field.options?.length ? field.options : undefined,
        group: field.group || undefined,
      };
    },

    // --- Field operations ---
    handleFieldDrop({ type, pageNumber, dropXPt, dropYPt }) {
      if (!this.activeDocument) return;
      const activeSigner = this.signers[this.activeSignerIndex];

      // Look up the page's metadata so we can scale defaults to its body-
      // text line height (Layer 2 - same input the server resolver uses).
      // Falls back to the page's height fraction when the page has no text
      // layer, then to FALLBACK_FIELD_RULE if rules haven't loaded yet.
      const page = (this.activeDocument.pages || []).find(p => p.pageNumber === pageNumber);
      const { widthPt, heightPt } = resolveAnnotationDefaults(
        type,
        page,
        this.fieldTypeRules,
        this.fieldTypeFallbackFraction
      );

      // Center the field on the drop point and clamp into page bounds.
      // PdfPageViewer doesn't know dimensions - it just emitted the click
      // location - so positioning lives with the resolver here.
      const pageWidthPt = page?.widthPt || 612;
      const pageHeightPt = page?.heightPt || 792;
      const x = Math.min(Math.max(dropXPt - widthPt / 2, 0), Math.max(0, pageWidthPt - widthPt));
      const y = Math.min(Math.max(dropYPt - heightPt / 2, 0), Math.max(0, pageHeightPt - heightPt));

      const field = {
        id: uuidv4(),
        documentId: this.activeDocument.documentId,
        type,
        signerIndex: this.activeSignerIndex,
        colorIndex: activeSigner?.colorIndex ?? this.activeSignerIndex,
        pageNumber,
        x,
        y,
        widthPt,
        heightPt,
        required: type !== 'Note',
        mustBeChecked: true,
        label: null,
        defaultValue: type === 'Note' ? '' : undefined,
        options: (type === 'Dropdown' || type === 'RadioGroup') ? [] : undefined,
        group: type === 'RadioGroup' ? `group-${uuidv4().substring(0, 8)}` : undefined,
      };

      this.fields.push(field);
      this.selectedFieldId = field.id;
      this.emitChanged();
      this._saveQueue.enqueueCreate(field.id, this.fieldToApiCreate(field));
    },

    handleFieldMove({ id, x, y }) {
      const field = this.fields.find(f => f.id === id);
      if (!field) return;
      field.x = x;
      field.y = y;
      this.emitChanged();
      this._saveQueue.enqueueUpdate(id, this.fieldToApiUpdate(field));
    },

    handleFieldResize({ id, x, y, widthPt, heightPt }) {
      const field = this.fields.find(f => f.id === id);
      if (!field) return;
      field.x = x;
      field.y = y;
      field.widthPt = widthPt;
      field.heightPt = heightPt;
      this.emitChanged();
      this._saveQueue.enqueueUpdate(id, this.fieldToApiUpdate(field));
    },

    handleFieldUpdate({ id, ...changes }) {
      const field = this.fields.find(f => f.id === id);
      if (!field) return;
      console.log('[FieldEditor.handleFieldUpdate] id=', id, 'changes=', JSON.stringify(changes));
      const allowed = ['signerIndex', 'label', 'required', 'mustBeChecked', 'options', 'defaultValue'];
      for (const key of allowed) {
        if (key in changes) field[key] = changes[key];
      }
      console.log('[FieldEditor.handleFieldUpdate] field.options after=', JSON.stringify(field.options));
      // Sync colorIndex when signer assignment changes
      if ('signerIndex' in changes) {
        const signer = this.signers[changes.signerIndex];
        field.colorIndex = signer?.colorIndex ?? changes.signerIndex;
      }
      this.emitChanged();
      this._saveQueue.enqueueUpdate(id, this.fieldToApiUpdate(field));
    },

    handleFieldDelete(fieldId) {
      const index = this.fields.findIndex(f => f.id === fieldId);
      if (index === -1) return;
      this.fields.splice(index, 1);
      if (this.selectedFieldId === fieldId) {
        this.selectedFieldId = null;
      }
      this.emitChanged();
      this._saveQueue.enqueueDelete(fieldId);
    },

    emitChanged() {
      this.$emit('annotations-changed', this.fields.slice());
    },

    // --- Page refs for Konva hit-testing ---
    setPageRef(pageNumber, el) {
      if (!this._pageRefs) this._pageRefs = {};
      if (el) this._pageRefs[pageNumber] = el;
      else delete this._pageRefs[pageNumber];
    },
    /**
     * Returns true when the pointerdown target is a Konva field that should
     * be dragged by Konva instead of triggering drag-scroll.
     */
    isFieldTarget(e) {
      if (e.target.tagName !== 'CANVAS') return false;
      for (const pv of Object.values(this._pageRefs || {})) {
        if (pv?.hasFieldAtPoint?.(e.clientX, e.clientY)) return true;
      }
      return false;
    },

    // --- Zoom ---
    zoomIn() {
      this.scale = Math.min(3, +(this.scale + 0.25).toFixed(2));
    },
    zoomOut() {
      this.scale = Math.max(0.5, +(this.scale - 0.25).toFixed(2));
    },
    zoomFit() {
      this.$refs.docViewer?.fitToContainer();
    },
  },
};
</script>

<style scoped>
.field-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.editor-topbar {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
  background: #fff;
  gap: 12px;
}

.slow-warning {
  white-space: nowrap;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.zoom-controls {
  display: flex;
  align-items: center;
}

.editor-content {
  display: flex;
  flex: 1;
  min-height: 0;
}

.editor-center {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}
</style>
